/**
 *  @class
 *  @function LineItemProperty
 *
 *  Wraps a "Line item property" product block and shows/hides it depending on
 *  the variant that is currently selected. The list of variant ids the field is
 *  hidden for is rendered by Liquid into `data-hide-variants`.
 *
 *  While hidden, the inputs are disabled (so they are neither validated nor
 *  submitted) and their `required` attribute is removed, which keeps
 *  `form.reportValidity()` from blocking on a field the customer cannot see.
 */
if (!customElements.get('line-item-property')) {
  class LineItemProperty extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.hiddenVariantIds = (this.dataset.hideVariants || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);

      this.fields = Array.from(this.querySelectorAll('input, textarea, select'));
      this.fields.forEach((field) => {
        if (field.hasAttribute('required') || field.dataset.lipRequired === 'true') {
          field.dataset.lipRequired = 'true';
        }
      });

      this.variants = this.parseVariants();
      this.picker = document.getElementById(`variant-selects-${this.sectionId}`);
      this.onVariantChange = this.update.bind(this);

      if (this.picker) {
        this.picker.addEventListener('change', this.onVariantChange);
      }
      // The theme also fires this once its variant fetch has settled.
      document.addEventListener('product:variant-change', this.onVariantChange);

      this.update();
    }

    disconnectedCallback() {
      if (this.picker) {
        this.picker.removeEventListener('change', this.onVariantChange);
      }
      document.removeEventListener('product:variant-change', this.onVariantChange);
    }

    parseVariants() {
      const data = this.querySelector('.line-item-property__variant-data');
      if (!data) return [];
      try {
        return JSON.parse(data.textContent) || [];
      } catch (e) {
        return [];
      }
    }

    selectedOptions() {
      if (!this.picker) return [];
      const options = [];
      this.picker.querySelectorAll('fieldset').forEach((fieldset) => {
        const select = fieldset.querySelector('select');
        if (select) {
          options.push(select.value);
          return;
        }
        const checked = fieldset.querySelector('input:checked');
        if (checked) {
          options.push(checked.value);
        }
      });
      return options;
    }

    currentVariantId() {
      const options = this.selectedOptions();

      if (options.length) {
        const match = this.variants.find((variant) => {
          return (
            Array.isArray(variant.options) &&
            variant.options.length === options.length &&
            variant.options.every((option, i) => option === options[i])
          );
        });
        if (match) return String(match.id);
      }

      // Single variant products, or an option combination that does not exist.
      const form = document.getElementById(`product-form-${this.sectionId}`);
      const input = form && form.querySelector('[name="id"]');
      return input ? String(input.value) : '';
    }

    update() {
      const hide = this.hiddenVariantIds.indexOf(this.currentVariantId()) !== -1;

      this.toggleAttribute('hidden', hide);

      this.fields.forEach((field) => {
        field.disabled = hide;
        if (field.dataset.lipRequired !== 'true') return;
        if (hide) {
          field.removeAttribute('required');
        } else {
          field.setAttribute('required', 'required');
        }
      });
    }
  }

  customElements.define('line-item-property', LineItemProperty);
}
